import { useCallback, useMemo, useState } from "react";

// Max portrait ratio: 3:5 (0.6) - images taller than this get capped
// This prevents extremely tall images (1:3, 1:4) from dominating the grid
// while allowing standard portrait fashion shots (2:3 ratio = 0.667) to display naturally
const MIN_ASPECT_RATIO = 0.6; // width/height = 3/5
const CAPPED_TALL_RATIO = "3 / 5";
const CAPPED_WIDE_RATIO = "1 / 1";

/**
 * Cap aspect ratio to prevent extreme dimensions.
 * Wide images (ratio > 1) are capped to square (1:1).
 * Tall images (ratio < MIN_ASPECT_RATIO) are capped to 3:5.
 */
function capAspectRatio(ratio: number): string | null {
  if (ratio > 1) return CAPPED_WIDE_RATIO;
  if (ratio < MIN_ASPECT_RATIO) return CAPPED_TALL_RATIO;
  return null; // No capping needed
}

interface ImageMetadata {
  width?: number;
  height?: number;
}

// Type for product image objects
type ImageObject = {
  url?: string | null;
  gs_url?: string | null;
  width?: number | null;
  w?: number | null;
  height?: number | null;
  h?: number | null;
};

// Type for products with image fields
type ProductWithImages = {
  images?: (string | ImageObject)[] | null;
  image?: string | ImageObject | null;
};

/**
 * Custom hook to handle product image metadata and aspect ratio calculations.
 * Separates image display logic from the ProductCard component.
 */
export function useProductImageMetadata(product: ProductWithImages | null) {
  const [measuredRatio, setMeasuredRatio] = useState<number | null>(null);

  // Calculate primary image metadata for aspect ratio (if available)
  const resolveImageObj = useMemo(() => {
    if (!product) return undefined;
    if (product.images && product.images.length > 0 && typeof product.images[0] === "object") {
      return product.images[0] as ImageObject;
    }
    if (product.image && typeof product.image === "object") {
      return product.image as ImageObject;
    }
    return undefined;
  }, [product]);

  // Detect if primary image is served from Google Cloud Storage
  const isGcsImage = useMemo(() => {
    if (!resolveImageObj) return false;
    if (typeof resolveImageObj === "object" && resolveImageObj !== null) {
      const urlStr = (resolveImageObj.gs_url ?? resolveImageObj.url) as string | undefined;
      return typeof urlStr === "string" && urlStr.includes("storage.googleapis.com");
    }
    return false;
  }, [resolveImageObj]);

  // Extract intrinsic dimensions only for GCS images (trusted metadata)
  const primaryImageMeta: ImageMetadata = useMemo(() => {
    if (!isGcsImage || !resolveImageObj || typeof resolveImageObj !== "object") {
      return { width: undefined, height: undefined };
    }
    const imgObj = resolveImageObj as {
      width?: number;
      w?: number;
      height?: number;
      h?: number;
    };
    return {
      width:
        typeof imgObj.width === "number"
          ? imgObj.width
          : typeof imgObj.w === "number"
            ? imgObj.w
            : undefined,
      height:
        typeof imgObj.height === "number"
          ? imgObj.height
          : typeof imgObj.h === "number"
            ? imgObj.h
            : undefined,
    };
  }, [isGcsImage, resolveImageObj]);

  // Cap images that are too wide or too tall
  const containerAspectRatio = useMemo(() => {
    if (!primaryImageMeta.width || !primaryImageMeta.height) return undefined;
    const ratio = primaryImageMeta.width / primaryImageMeta.height;
    return capAspectRatio(ratio) ?? `${primaryImageMeta.width} / ${primaryImageMeta.height}`;
  }, [primaryImageMeta]);

  const imageWrapperClass = useMemo(
    () =>
      `relative w-full overflow-hidden select-none ` +
      (containerAspectRatio ? "" : "aspect-square"),
    [containerAspectRatio]
  );

  const dynamicAspectRatio = useMemo(() => {
    if (containerAspectRatio) return containerAspectRatio;
    if (measuredRatio) {
      return capAspectRatio(measuredRatio) ?? `${measuredRatio.toFixed(3)} / 1`;
    }
    return undefined;
  }, [containerAspectRatio, measuredRatio]);

  const objectFit = useMemo(() => {
    // Use "contain" when we have image metadata (works for both portrait and landscape)
    // This prevents extreme zoom/crop on landscape images in square containers
    // Fall back to "cover" only when no metadata is available
    return measuredRatio || containerAspectRatio ? "contain" : "cover";
  }, [measuredRatio, containerAspectRatio]);

  // Determine if image needs white background (when it's been capped due to extreme tall aspect ratio)
  // Derived from dynamicAspectRatio to avoid redundant ratio calculation
  const needsWhiteBackground = dynamicAspectRatio === CAPPED_TALL_RATIO;

  // Callback for when image loads to measure actual dimensions
  const handleImageLoad = useCallback(
    (img: { naturalWidth: number; naturalHeight: number }) => {
      if (!containerAspectRatio && !measuredRatio) {
        const ratio = img.naturalWidth / img.naturalHeight;
        if (ratio && isFinite(ratio)) setMeasuredRatio(ratio);
      }
    },
    [containerAspectRatio, measuredRatio]
  );

  return {
    imageWrapperClass,
    dynamicAspectRatio,
    objectFit,
    handleImageLoad,
    measuredRatio,
    needsWhiteBackground,
  };
}
